import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { ChevronDown, Loader2, MessageCircle, Maximize2, Minimize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCrispChatboxHidden } from "@/hooks/useCrisp";
import { cn } from "@/lib/utils";
import { useAssistantDockStore } from "@/stores/assistantDockStore";
import { compareConversationProjectionOrder } from "@/utils/conversationProjection";
import type { Conversation } from "@/types/chat";
import { useConversation } from "@/hooks/useConversationsAPI";
import { useBasculeDuDockVersLAssistant } from "../hooks/useBasculeDuDockVersLAssistant";
import { useGlobalConversationList } from "../hooks/useGlobalConversationList";
import type { CaseContextMode } from "../types/tools.types";
import { formatAdaptiveTimestamp } from "../utils/formatTimestamp";
import { BandeauDeBasculeDuDock } from "./BandeauDeBasculeDuDock";
import { V3ChatInterface } from "./V3ChatInterface";
import { useAgentUiActionHost } from "../ui-actions";
import { useActiveSessionsStore } from "../stores/activeSessionsStore";
import { resolveConversationComposerAccess } from "../utils/conversationComposerAccess";
import { usePublierConversationAffichee } from '../state/conversationAffichee';
import type { BrouillonComposeur } from '../state/brouillonComposeurRelais';
import {
  DOSSIER_QUICK_ACTIONS,
  getDossierQuickAction,
  type DossierQuickActionId,
} from './dossierQuickActions';

const FLOATING_ASSISTANT_SNAP_DISTANCE_PX = 16;
const FLOATING_ASSISTANT_DEFAULT_SIZE: FloatingSize = { width: 400, height: 704 };
const FLOATING_ASSISTANT_MIN_SIZE: FloatingSize = { width: 340, height: 420 };
const DOSSIER_ASSISTANT_RECENT_CONVERSATIONS_LIMIT = 6;

/** Active-only, pin-first projection used by the compact dossier switcher. */
export function buildRecentDossierConversations(conversations: Conversation[]): Conversation[] {
  return conversations
    .filter((conversation) => !conversation.isArchived)
    .slice()
    .sort(compareConversationProjectionOrder)
    .slice(0, DOSSIER_ASSISTANT_RECENT_CONVERSATIONS_LIMIT);
}

export type DossierAssistantPlacement = "docked" | "floating";

type FloatingPosition = {
  x: number;
  y: number;
};

type FloatingSize = {
  width: number;
  height: number;
};

type SnapTarget = "top" | "right" | "bottom" | "left" | "top-left" | "top-right" | "bottom-left" | "bottom-right";

type DragState = {
  pointerId: number;
  pointerOrigin: FloatingPosition;
  panelOrigin: FloatingPosition;
  size: FloatingSize;
};

type ResizeCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

type ResizeState = {
  pointerId: number;
  corner: ResizeCorner;
  pointerOrigin: FloatingPosition;
  panelOrigin: FloatingPosition;
  sizeOrigin: FloatingSize;
};

interface DossierCaseDiscoveryShellProps {
  /**
   * Dossier hôte, ou `null` quand le panneau est ouvert depuis une page sans
   * dossier (Personnalisation, Réglages…). `null` n'est pas un cas dégradé :
   * c'est le dock hors dossier, qui vaut une conversation « Générale ».
   */
  caseId: string | null;
  /**
   * Raccourci métier pré-armé par l'hôte dossier (`CaseView`). Sans objet hors
   * dossier : les raccourcis eux-mêmes ne sont pas rendus (cf. `quickActions`).
   */
  initialQuickActionId?: DossierQuickActionId;
  onClose: () => void;
  placement: DossierAssistantPlacement;
  onPlacementChange: (placement: DossierAssistantPlacement) => void;
}

/**
 * Real Case Discovery chat embedded in a dossier — or in the app shell when the
 * attorney asks Hector from a settings-type page (`caseId === null`).
 *
 * The host owns the placement and panel dimensions so the surrounding chrome can
 * resize. This shell keeps the live conversation, session and floating
 * coordinates in one mounted subtree: detaching must never reset a draft or WS.
 */
export function DossierCaseDiscoveryShell({
  caseId,
  initialQuickActionId,
  onClose,
  placement,
  onPlacementChange,
}: DossierCaseDiscoveryShellProps) {
  const [activeQuickActionId, setActiveQuickActionId] = useState<DossierQuickActionId | null>(
    initialQuickActionId ?? null,
  );
  const [composerDraft, setComposerDraft] = useState<{ content: string } | null>(
    () => initialQuickActionId
      ? { content: getDossierQuickAction(initialQuickActionId).prompt }
      : null,
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const brouillonComposeurRef = useRef<BrouillonComposeur>({ content: '', files: [] });
  const memoriserBrouillonComposeur = useCallback((brouillon: BrouillonComposeur) => {
    brouillonComposeurRef.current = brouillon;
  }, []);
  const lireBrouillonComposeur = useCallback(() => brouillonComposeurRef.current, []);

  // Ce chat affiche un fil sans jamais toucher à l'URL : sans cette déclaration,
  // l'avertisseur de fin de tour — monté dans la coque depuis #859 — préviendrait
  // l'avocat de la fin d'un tour qu'il est en train de regarder.
  usePublierConversationAffichee(conversationId);
  // The host dossier is only the initial suggestion. The attorney may use a
  // different dossier for the next turn without navigating away from this page.
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(caseId);
  // Ce chat n'offre aucun emplacement au canvas (#860). Quand Hector PREND le
  // document de ce fil, le dock publie la scène du canvas pour lui ; quand il y
  // ÉCRIT, l'avocat rejoint l'assistant pour le voir (#1588). Afficher un fil
  // ne publie rien : la résidence du canvas réhydraterait ou refroidirait des
  // documents sur une page qui n'en montre aucun.
  // L'annonce rendue ici est la transition douce : le dock la pose, s'estompe,
  // puis la bascule part. Elle vaut `null` hors de cette fenêtre d'attente.
  const annonceDeBascule = useBasculeDuDockVersLAssistant({
    conversationId,
    sessionKey,
    caseId: selectedCaseId,
    lireBrouillonComposeur,
  });
  // Demande posée par l'hôte au moment d'ouvrir le dock (barre « Demander à
  // Hector » d'une page de réglages). On la tient en state LOCAL plutôt que de
  // lire le store directement : `hostSubmission` doit garder une identité stable
  // jusqu'à la prochaine demande, sinon la remise à null du store repasserait
  // dans l'effet de V3ChatInterface.
  const [hostSubmission, setHostSubmission] = useState<{ content: string; nonce: number } | null>(null);
  const dockPrompt = useAssistantDockStore((state) => state.prompt);
  const consumeDockPrompt = useAssistantDockStore((state) => state.consumePrompt);
  const appliedPromptNonceRef = useRef<number | null>(null);
  const [floatingPosition, setFloatingPosition] = useState<FloatingPosition | null>(null);
  const [floatingSize, setFloatingSize] = useState<FloatingSize>(FLOATING_ASSISTANT_DEFAULT_SIZE);
  const [snapTarget, setSnapTarget] = useState<SnapTarget | null>(null);
  const [snappedEdge, setSnappedEdge] = useState<SnapTarget | null>(null);
  const shellRef = useRef<HTMLElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const resizeStateRef = useRef<ResizeState | null>(null);
  const isFloating = placement === "floating";

  // Le chatbox Crisp est en `position: fixed` en bas à droite du VIEWPORT, donc
  // hors de la grille de l'app : docké à droite, ce panneau lui donne rendez-vous
  // pile sur son bouton d'envoi, et l'avocat ne peut plus envoyer sa consigne
  // (signalement produit 2026-08-14 depuis la Personnalisation). La réservation
  // vit ici, avec le composer qu'elle protège, plutôt que dans chacun des deux
  // points de montage du dock (`CaseView`, `DashboardLayout`) qui ne rendent ce
  // shell que dock ouvert : une seule règle, valable aussi en flottant, où le
  // panneau peut être déplacé au-dessus de la bulle.
  useCrispChatboxHidden("assistant-dock");
  const { conversations, isLoading: isLoadingConversations } = useGlobalConversationList();
  const agentUiActionHost = useAgentUiActionHost({
    selectedCaseId,
    routeCaseId: caseId ?? undefined,
    // Hors dossier, la page hôte n'expose aucune surface dossier : exécuter une
    // action dossier localement n'ouvrirait rien. Même raison que `/assistant`.
    alwaysHandoffCaseActions: caseId === null,
  });
  const recentConversations = useMemo(
    () => buildRecentDossierConversations(conversations),
    [conversations],
  );
  const activeConversation = useMemo(
    () => recentConversations.find((conversation) => conversation.id === conversationId)
      ?? conversations.find((conversation) => conversation.id === conversationId)
      ?? null,
    [conversationId, conversations, recentConversations],
  );

  const openConversation = (conversation: Conversation) => {
    setActiveQuickActionId(null);
    setComposerDraft({ content: '' });
    setConversationId(conversation.id);
    setSessionKey(null);
    // Un fil « Général » (`caseId === null`) reste général. Le repli historique
    // sur le dossier hôte re-scopait silencieusement une conversation que
    // l'avocat avait justement ouverte hors dossier.
    setSelectedCaseId(conversation.caseId);
  };

  const startFreshConversation = () => {
    setActiveQuickActionId(null);
    setComposerDraft({ content: '' });
    setConversationId(null);
    setSessionKey(null);
    setSelectedCaseId(caseId);
  };

  /**
   * Demande de l'hôte → `V3ChatInterface`, qui l'**envoie** (décision produit
   * 2026-08-12, cf. `assistantDockStore`). Le shell ne fait que la transporter :
   * c'est le chat qui possède le cycle d'envoi et sa garde d'unicité.
   *
   * Consommée une fois, et corrélée par `nonce` plutôt que par un lazy-init de
   * state : le dock reste monté entre deux demandes, donc une seconde barre
   * « Demander à Hector » doit pouvoir viser un panneau déjà ouvert. Le ref rend
   * l'effet idempotent (StrictMode double-invoque avec la même valeur de
   * `dockPrompt`), et le `nonce` est reconduit tel quel jusqu'au chat pour que
   * les deux étages partagent la MÊME clé d'idempotence.
   */
  useEffect(() => {
    if (!dockPrompt || appliedPromptNonceRef.current === dockPrompt.nonce) return;
    appliedPromptNonceRef.current = dockPrompt.nonce;
    setHostSubmission({ content: dockPrompt.content, nonce: dockPrompt.nonce });
    consumeDockPrompt();
  }, [consumeDockPrompt, dockPrompt]);

  useEffect(() => {
    if (!initialQuickActionId) return;
    const focusFrame = requestAnimationFrame(() => {
      shellRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
    });
    return () => cancelAnimationFrame(focusFrame);
  }, [initialQuickActionId]);

  useEffect(() => {
    if (!isFloating) {
      dragStateRef.current = null;
      resizeStateRef.current = null;
      setSnapTarget(null);
      return;
    }

    const keepFloatingWindowVisible = () => {
      const shell = shellRef.current;
      if (!shell) return;

      const bounds = shell.getBoundingClientRect();
      const size = clampFloatingSize({ width: bounds.width, height: bounds.height });
      setFloatingSize(size);
      setFloatingPosition((position) => clampFloatingPosition(
        position ?? { x: bounds.left, y: bounds.top },
        size,
      ));
    };

    window.addEventListener("resize", keepFloatingWindowVisible);
    return () => window.removeEventListener("resize", keepFloatingWindowVisible);
  }, [isFloating]);

  const floatingStyle: CSSProperties | undefined = isFloating
    ? floatingPosition
      ? { left: floatingPosition.x, top: floatingPosition.y, width: floatingSize.width, height: floatingSize.height }
      : { right: FLOATING_ASSISTANT_SNAP_DISTANCE_PX, top: FLOATING_ASSISTANT_SNAP_DISTANCE_PX, width: floatingSize.width, height: floatingSize.height }
    : undefined;

  const handleDragStart = (event: ReactPointerEvent<HTMLElement>) => {
    if (!isFloating || event.button !== 0 || isDragControl(event.target)) return;

    const shell = shellRef.current;
    if (!shell) return;

    const bounds = shell.getBoundingClientRect();
    const panelOrigin = clampFloatingPosition(
      { x: bounds.left, y: bounds.top },
      { width: bounds.width, height: bounds.height },
    );

    dragStateRef.current = {
      pointerId: event.pointerId,
      pointerOrigin: { x: event.clientX, y: event.clientY },
      panelOrigin,
      size: { width: bounds.width, height: bounds.height },
    };
    setFloatingPosition(panelOrigin);
    setSnapTarget(null);
    setSnappedEdge(null);
    capturePointerIfAvailable(event.currentTarget, event.pointerId);
  };

  const handleDragMove = (event: ReactPointerEvent<HTMLElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const position = positionFromPointerEvent(event, dragState);
    setFloatingPosition(position);
    setSnapTarget(findSnapTarget(position, dragState.size));
  };

  const handleDragEnd = (event: ReactPointerEvent<HTMLElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const position = positionFromPointerEvent(event, dragState);
    const nextSnapTarget = findSnapTarget(position, dragState.size);
    setFloatingPosition(applySnap(position, dragState.size, nextSnapTarget));
    setSnapTarget(null);
    setSnappedEdge(nextSnapTarget);
    dragStateRef.current = null;

    releasePointerIfCaptured(event.currentTarget, event.pointerId);
  };

  const handleResizeStart = (corner: ResizeCorner) => (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!isFloating || event.button !== 0) return;

    const shell = shellRef.current;
    if (!shell) return;

    const bounds = shell.getBoundingClientRect();
    const sizeOrigin = clampFloatingSize({ width: bounds.width, height: bounds.height });
    const panelOrigin = clampFloatingPosition({ x: bounds.left, y: bounds.top }, sizeOrigin);

    resizeStateRef.current = {
      pointerId: event.pointerId,
      corner,
      pointerOrigin: { x: event.clientX, y: event.clientY },
      panelOrigin,
      sizeOrigin,
    };
    setFloatingPosition(panelOrigin);
    setFloatingSize(sizeOrigin);
    setSnapTarget(null);
    setSnappedEdge(null);
    capturePointerIfAvailable(event.currentTarget, event.pointerId);
    event.stopPropagation();
  };

  const handleResizeMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;

    const nextFrame = resizeFrameFromPointerEvent(event, resizeState);
    setFloatingPosition(nextFrame.position);
    setFloatingSize(nextFrame.size);
    event.stopPropagation();
  };

  const handleResizeEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;

    const nextFrame = resizeFrameFromPointerEvent(event, resizeState);
    setFloatingPosition(nextFrame.position);
    setFloatingSize(nextFrame.size);
    resizeStateRef.current = null;

    releasePointerIfCaptured(event.currentTarget, event.pointerId);
    event.stopPropagation();
  };

  /**
   * `host_selectable` = « l'hôte a proposé un dossier, seul l'avocat peut en
   * changer ». La politique n'a de sens que tant qu'un dossier est effectivement
   * visé : le dock hors dossier et un fil « Général » ouvert depuis le switcher
   * relèvent de la politique du global assistant. Sans ça, `V3ChatInterface`
   * lève à l'affichage (il exige un `selectedCaseId` hors `selectable`).
   */
  const caseContextMode: CaseContextMode = caseId && selectedCaseId ? "host_selectable" : "selectable";
  // Le fil ouvert ici peut être partagé en lecture : même décision que `/assistant`.
  const isPendingConversation = useActiveSessionsStore((state) => (
    conversationId ? state.pendingConversations[conversationId] !== undefined : false
  ));
  const isAuthoredInSession = useActiveSessionsStore((state) => (
    conversationId ? state.authoredConversations[conversationId] !== undefined : false
  ));
  const {
    data: conversationDetail,
    isError: hasConversationDetailError,
    refetch: refetchConversationDetail,
  } = useConversation(conversationId ?? '', { enabled: conversationId !== null && !isPendingConversation });
  const composerAccessState = resolveConversationComposerAccess({
    conversationId,
    isPendingConversation,
    isAuthoredInSession,
    detailRole: conversationDetail?.my_role,
    fallbackRole: activeConversation?.my_role,
    hasDetailError: hasConversationDetailError,
  });
  const surfaceLabel = caseId ? "l'assistant du dossier" : "l'assistant";

  return (
    <aside
      ref={shellRef}
      aria-label={caseId ? "Assistant du dossier" : "Assistant Hector"}
      data-placement={placement}
      data-snap-target={snapTarget ?? undefined}
      data-snapped-edge={snappedEdge ?? undefined}
      style={floatingStyle}
      className={cn(
        "flex min-h-0 min-w-0 flex-col overflow-hidden bg-white",
        isFloating
          ? "fixed z-40 rounded-xl border border-grey-200 shadow-[0_12px_32px_rgba(15,23,42,0.16)]"
          : "relative z-10 h-full w-full",
      )}
    >
      <header
        data-testid="assistant-drag-region"
        className={cn(
          "flex shrink-0 items-center justify-between gap-2 border-b border-grey-100 px-3 py-2.5",
          isFloating && "cursor-grab select-none touch-none active:cursor-grabbing",
        )}
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
      >
        <DossierConversationSwitcher
          activeConversationId={conversationId}
          activeConversation={activeConversation}
          recentConversations={recentConversations}
          isLoading={isLoadingConversations}
          hasHostCase={caseId !== null}
          onConversationSelect={openConversation}
          onStartFreshConversation={startFreshConversation}
        />

        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            // Détacher n'existe pas en fenêtre étroite : la fenêtre flottante
            // fait 400 × 704 px et déborderait d'un écran de téléphone.
            className={cn(
              "h-7 gap-1 px-2 text-xs text-grey-600 hover:bg-grey-100 hover:text-charcoal",
              !isFloating && "hidden md:inline-flex",
            )}
            onPointerDown={stopDragPropagation}
            onClick={() => onPlacementChange(isFloating ? "docked" : "floating")}
            aria-label={isFloating
              ? (caseId ? "Rattacher l'assistant au dossier" : "Rattacher l'assistant")
              : `Détacher ${surfaceLabel}`}
          >
            {isFloating ? <Minimize2 className="h-3.5 w-3.5" aria-hidden /> : <Maximize2 className="h-3.5 w-3.5" aria-hidden />}
            {isFloating ? "Rattacher" : "Détacher"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-grey-500 hover:bg-grey-100 hover:text-charcoal"
            onPointerDown={stopDragPropagation}
            onClick={onClose}
            aria-label={`Fermer ${surfaceLabel}`}
            title={`Fermer ${surfaceLabel}`}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </header>

      {annonceDeBascule && (
        <BandeauDeBasculeDuDock onOuvrirMaintenant={annonceDeBascule.ouvrirMaintenant} />
      )}

      <div
        data-testid="dock-conversation"
        className={cn(
          "flex-1 min-h-0 min-w-0",
          // Le fondu porte sur la CONVERSATION, pas sur le panneau entier :
          // l'opacité CSS s'hérite, et un bandeau estompé serait illisible au
          // moment précis où il faut le lire. Opacité + translation seules —
          // aucune propriété de mise en page, donc aucun reflow — et
          // `motion-reduce` coupe le mouvement pour qui l'a demandé, à la
          // manière des `animate-* motion-reduce:animate-none` de la maison.
          "transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none",
          annonceDeBascule && "-translate-y-1 opacity-40 motion-reduce:translate-y-0",
        )}
      >
        <V3ChatInterface
          conversationRouteOwner="host"
          hostCaseId={caseId}
          composerDraft={composerDraft}
          onComposerDraftChange={memoriserBrouillonComposeur}
          uiActionCapabilities={agentUiActionHost.uiActionCapabilities}
          onUiAction={agentUiActionHost.onUiAction}
          embedded
          caseContextMode={caseContextMode}
          conversationId={conversationId}
          sessionKey={sessionKey}
          onSessionKeyChange={setSessionKey}
          selectedCaseId={selectedCaseId}
          onCaseSelect={setSelectedCaseId}
          onConversationCreated={setConversationId}
          composerAccessState={composerAccessState}
          onComposerAccessRetry={() => { void refetchConversationDetail(); }}
          hostSubmission={hostSubmission}
          embeddedEmptyState={{
            // Les prompts métier restent propres au shell dossier : la grille
            // personnelle est éditable et doit rester réservée à /assistant.
            // Hors dossier, ces cinq raccourcis parlent tous « de ce dossier »
            // et n'ont donc aucun sens : l'accueil décoratif prend le relais.
            quickActions: caseId ? DOSSIER_QUICK_ACTIONS : undefined,
            initialQuickActionId: activeQuickActionId ?? undefined,
          }}
        />
      </div>

      {isFloating && (
        <>
          {(["top-left", "top-right", "bottom-left", "bottom-right"] as const).map((corner) => (
            <button
              key={corner}
              type="button"
              data-no-drag
              data-testid={`assistant-resize-${corner}`}
              aria-label={`Redimensionner l'assistant (${corner})`}
              className={cn(
                "absolute z-10 h-5 w-5 rounded-sm opacity-0 transition-opacity hover:bg-grey-200/70 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal",
                corner.includes("top") ? "-top-1" : "-bottom-1",
                corner.includes("left") ? "-left-1" : "-right-1",
                corner === "top-left" || corner === "bottom-right" ? "cursor-nwse-resize" : "cursor-nesw-resize",
              )}
              onPointerDown={handleResizeStart(corner)}
              onPointerMove={handleResizeMove}
              onPointerUp={handleResizeEnd}
              onPointerCancel={handleResizeEnd}
            />
          ))}
        </>
      )}
    </aside>
  );
}

function DossierConversationSwitcher({
  activeConversationId,
  activeConversation,
  recentConversations,
  isLoading,
  hasHostCase,
  onConversationSelect,
  onStartFreshConversation,
}: {
  activeConversationId: string | null;
  activeConversation: Conversation | null;
  recentConversations: Conversation[];
  isLoading: boolean;
  /** Décide du libellé de création : « dans ce dossier » n'existe pas hors dossier. */
  hasHostCase: boolean;
  onConversationSelect: (conversation: Conversation) => void;
  onStartFreshConversation: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          data-no-drag
          className="h-7 min-w-0 max-w-[13rem] justify-start gap-1.5 rounded-md px-2 text-[10px] font-medium uppercase text-grey-600 hover:bg-grey-100 hover:text-charcoal"
          onPointerDown={stopDragPropagation}
          aria-label="Ouvrir les dernières conversations"
        >
          <span className="truncate">Mes conversations</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-grey-500" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-[21rem] max-w-[calc(100vw-2rem)] p-1.5"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <DropdownMenuLabel className="px-2 py-1 text-[10px] font-medium uppercase text-grey-500">
          Dernières conversations
        </DropdownMenuLabel>

        {isLoading ? (
          <div className="flex items-center gap-2 px-2 py-3 text-xs text-grey-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Chargement...
          </div>
        ) : recentConversations.length === 0 ? (
          <div className="px-2 py-3 text-xs text-grey-500">
            Aucune conversation récente.
          </div>
        ) : (
          recentConversations.map((conversation) => (
            <DropdownMenuItem
              key={conversation.id}
              className={cn(
                "cursor-pointer items-start gap-2 rounded-md px-2 py-2 focus:bg-grey-100",
                conversation.id === activeConversationId && "bg-grey-100",
              )}
              onSelect={() => onConversationSelect(conversation)}
            >
              <MessageCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-grey-500" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium leading-tight text-charcoal">
                  {conversationTitle(conversation)}
                </span>
                <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] leading-tight text-grey-500">
                  <span className="truncate">{conversation.caseTitle ?? "Général"}</span>
                  <span aria-hidden>·</span>
                  <span className="shrink-0">{formatAdaptiveTimestamp(conversation.lastMessageAt)}</span>
                </span>
              </span>
            </DropdownMenuItem>
          ))
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer rounded-md px-2 py-2 text-[13px] text-grey-700 focus:bg-grey-100 focus:text-charcoal"
          onSelect={onStartFreshConversation}
        >
          {hasHostCase ? "Nouvelle conversation dans ce dossier" : "Nouvelle conversation"}
        </DropdownMenuItem>

        {activeConversation && (
          <div className="border-t border-grey-100 px-2 py-1.5 text-[11px] text-grey-500">
            Fil ouvert : <span className="font-medium text-grey-700">{conversationTitle(activeConversation)}</span>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function conversationTitle(conversation: Conversation): string {
  return conversation.title || conversation.caseTitle || "Conversation sans titre";
}

function positionFromPointerEvent(event: ReactPointerEvent<HTMLElement>, dragState: DragState): FloatingPosition {
  return clampFloatingPosition(
    {
      x: dragState.panelOrigin.x + event.clientX - dragState.pointerOrigin.x,
      y: dragState.panelOrigin.y + event.clientY - dragState.pointerOrigin.y,
    },
    dragState.size,
  );
}

function clampFloatingPosition(position: FloatingPosition, size: FloatingSize): FloatingPosition {
  return {
    x: clamp(position.x, 0, Math.max(0, window.innerWidth - size.width)),
    y: clamp(position.y, 0, Math.max(0, window.innerHeight - size.height)),
  };
}

function clampFloatingSize(size: FloatingSize): FloatingSize {
  const maxWidth = Math.max(180, window.innerWidth - FLOATING_ASSISTANT_SNAP_DISTANCE_PX * 2);
  const maxHeight = Math.max(260, window.innerHeight - FLOATING_ASSISTANT_SNAP_DISTANCE_PX * 2);
  const minWidth = Math.min(FLOATING_ASSISTANT_MIN_SIZE.width, maxWidth);
  const minHeight = Math.min(FLOATING_ASSISTANT_MIN_SIZE.height, maxHeight);

  return {
    width: clamp(size.width, minWidth, maxWidth),
    height: clamp(size.height, minHeight, maxHeight),
  };
}

function resizeFrameFromPointerEvent(
  event: ReactPointerEvent<HTMLButtonElement>,
  resizeState: ResizeState,
): { position: FloatingPosition; size: FloatingSize } {
  const deltaX = event.clientX - resizeState.pointerOrigin.x;
  const deltaY = event.clientY - resizeState.pointerOrigin.y;
  const resizesLeft = resizeState.corner.endsWith("left");
  const resizesTop = resizeState.corner.startsWith("top");
  const rightEdge = resizeState.panelOrigin.x + resizeState.sizeOrigin.width;
  const bottomEdge = resizeState.panelOrigin.y + resizeState.sizeOrigin.height;

  const rawSize = {
    width: resizeState.sizeOrigin.width + (resizesLeft ? -deltaX : deltaX),
    height: resizeState.sizeOrigin.height + (resizesTop ? -deltaY : deltaY),
  };
  const size = clampFloatingSize(rawSize);
  const rawPosition = {
    x: resizesLeft ? rightEdge - size.width : resizeState.panelOrigin.x,
    y: resizesTop ? bottomEdge - size.height : resizeState.panelOrigin.y,
  };
  const position = clampFloatingPosition(rawPosition, size);

  return { position, size };
}

function findSnapTarget(position: FloatingPosition, size: FloatingSize): SnapTarget | null {
  const nearTop = position.y <= FLOATING_ASSISTANT_SNAP_DISTANCE_PX;
  const nearRight = window.innerWidth - (position.x + size.width) <= FLOATING_ASSISTANT_SNAP_DISTANCE_PX;
  const nearBottom = window.innerHeight - (position.y + size.height) <= FLOATING_ASSISTANT_SNAP_DISTANCE_PX;
  const nearLeft = position.x <= FLOATING_ASSISTANT_SNAP_DISTANCE_PX;

  if (nearTop && nearLeft) return "top-left";
  if (nearTop && nearRight) return "top-right";
  if (nearBottom && nearLeft) return "bottom-left";
  if (nearBottom && nearRight) return "bottom-right";
  if (nearTop) return "top";
  if (nearRight) return "right";
  if (nearBottom) return "bottom";
  if (nearLeft) return "left";
  return null;
}

function applySnap(position: FloatingPosition, size: FloatingSize, target: SnapTarget | null): FloatingPosition {
  if (!target) return position;

  const snapToTop = target === "top" || target.startsWith("top-");
  const snapToRight = target === "right" || target.endsWith("-right");
  const snapToBottom = target === "bottom" || target.startsWith("bottom-");
  const snapToLeft = target === "left" || target.endsWith("-left");

  return clampFloatingPosition(
    {
      x: snapToLeft
        ? FLOATING_ASSISTANT_SNAP_DISTANCE_PX
        : snapToRight
          ? window.innerWidth - size.width - FLOATING_ASSISTANT_SNAP_DISTANCE_PX
          : position.x,
      y: snapToTop
        ? FLOATING_ASSISTANT_SNAP_DISTANCE_PX
        : snapToBottom
          ? window.innerHeight - size.height - FLOATING_ASSISTANT_SNAP_DISTANCE_PX
          : position.y,
    },
    size,
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function isDragControl(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest("button, a, input, textarea, select, [data-no-drag]"));
}

function stopDragPropagation(event: ReactPointerEvent<HTMLButtonElement>): void {
  event.stopPropagation();
}

function capturePointerIfAvailable(element: Element, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Storybook/Vitest can dispatch synthetic pointer events without a browser
    // active-pointer record. Drag/resize state is already tracked in React refs,
    // so failing to capture in that environment must not break the interaction.
  }
}

function releasePointerIfCaptured(element: Element, pointerId: number): void {
  if (element.hasPointerCapture(pointerId)) {
    element.releasePointerCapture(pointerId);
  }
}
